// src/hooks/useSnappedTrails.js
//
// Per-device GPS buffer + batched call to /api/snap/match. Returns the latest
// snapped fix per device (for marker placement) and a snapped polyline per
// device (for trail line rendering). Gated by the `featureSnapToRoad` flag —
// off → returns empty maps so callers fall through to raw coords.
//
// Batching strategy (matches the plan): per device, buffer up to BUFFER_SIZE
// raw fixes. Call the server when either (a) we have >= MIN_FIXES_TO_SEND new
// fixes since the last call, OR (b) MAX_AGE_MS has elapsed since the last
// call. Cache the response in a Map so MapView can read it synchronously.

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import { isStationaryFix } from '@trackizy/formatters';
import useFeatures from '@/hooks/useFeatures';
import { snap as snapApi } from '@/lib/trackizy';
import { countSnapped } from '@/lib/snapProject';

// Per-user pref. Lives alongside `trackizy_livemap_animate_positions` so the
// two Live Map display prefs share the same namespace + UX surface (pill in
// the floating panel-edge controls).
const LS_KEY = 'trackizy_livemap_snap_to_road';
// Per-user snap-projection threshold (meters). Below this distance from the
// snapped polyline we accept the projection; above it we fall through to raw.
// Tuned via the gear popover next to the Snap pill.
const LS_THRESHOLD_KEY = 'trackizy_livemap_snap_threshold_m';
const DEFAULT_THRESHOLD_M = 50;
const MIN_THRESHOLD_M = 10;
const MAX_THRESHOLD_M = 500;

function clampThreshold(n) {
  if (!Number.isFinite(n)) return DEFAULT_THRESHOLD_M;
  return Math.max(MIN_THRESHOLD_M, Math.min(MAX_THRESHOLD_M, n));
}

function readThresholdSync() {
  try {
    const v = Number(localStorage.getItem(LS_THRESHOLD_KEY));
    return Number.isFinite(v) && v > 0 ? clampThreshold(v) : DEFAULT_THRESHOLD_M;
  } catch {
    return DEFAULT_THRESHOLD_M;
  }
}

// Trail history settings — SAME localStorage key as `useTrails.js`. Users
// configure these via the gear next to the Trails pill (maxPoints / maxMinutes /
// minZoom). The snapped trail respects the same caps so changing one knob
// affects both rails consistently.
const TRAILS_SETTINGS_KEY = 'trackizy_trails_settings';
const DEFAULT_TRAIL_SETTINGS = { maxPoints: 30, maxMinutes: 5, minZoom: 10 };

function readTrailSettingsSync() {
  try {
    const raw = localStorage.getItem(TRAILS_SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...DEFAULT_TRAIL_SETTINGS, ...parsed };
    }
  } catch {
    /* fall through */
  }
  return { ...DEFAULT_TRAIL_SETTINGS };
}

const BUFFER_SIZE = 10;          // how many raw fixes we keep per device
const MIN_FIXES_TO_SEND = 5;     // batch trigger: number of new fixes
const MAX_AGE_MS = 15_000;       // batch trigger: time since last call
const MAX_PER_TICK = 25;         // safety cap so a 1k-vehicle org doesn't fan out at once
const FETCH_DEBOUNCE_MS = 1_000; // collapse rapid WS bursts into one drain

// Module-scope state so subscribers across the page share buffers and
// snapshots — same convention as positionAnimator.js.
const buffers = new Map();              // deviceId → [{lng, lat, time}] (recent raw fixes sent to OSRM)
const lastCallAt = new Map();           // deviceId → ms-epoch
const lastSentCount = new Map();        // deviceId → buffer length at last call
const snappedLatest = new Map();        // deviceId → {latitude, longitude, fixTime}
const snappedPolyline = new Map();      // deviceId → GeoJSON LineString (derived from accumulatedTrail)
const accumulatedTrail = new Map();     // deviceId → [{lng, lat, time}] (stitched snap history, capped by trail settings)
const inFlight = new Set();             // deviceId set — prevent overlapping calls

// Module-scope pub/sub so passive subscribers (e.g. the snap-counter readout in
// the Live Map gear popover) can re-render whenever a snap batch lands, WITHOUT
// mounting a second `useSnappedTrails()` engine and doubling the OSRM fan-out.
// Same Set<listener> convention as positionAnimator.js / useTelemetryPrefs.js.
let snapVersion = 0;
const snapListeners = new Set();
function notifySnap() {
  snapVersion += 1;
  snapListeners.forEach((l) => l());
}
function subscribeSnap(listener) {
  snapListeners.add(listener);
  return () => snapListeners.delete(listener);
}
function getSnapVersion() {
  return snapVersion;
}

function appendToBuffer(deviceId, lng, lat, time) {
  let buf = buffers.get(deviceId);
  if (!buf) {
    buf = [];
    buffers.set(deviceId, buf);
  }
  const tail = buf[buf.length - 1];
  if (tail && Math.abs(tail.lng - lng) < 1e-7 && Math.abs(tail.lat - lat) < 1e-7) return false;
  buf.push({ lng, lat, time });
  while (buf.length > BUFFER_SIZE) buf.shift();
  return true;
}

function shouldCall(deviceId, now) {
  const buf = buffers.get(deviceId);
  if (!buf || buf.length < 2) return false;
  if (inFlight.has(deviceId)) return false;
  const lastCount = lastSentCount.get(deviceId) ?? 0;
  const newFixes = buf.length - lastCount;
  const lastAt = lastCallAt.get(deviceId) ?? 0;
  const aged = now - lastAt >= MAX_AGE_MS;
  return newFixes >= MIN_FIXES_TO_SEND || (newFixes >= 1 && aged);
}

function trimAccumulated(deviceId, settings) {
  const accum = accumulatedTrail.get(deviceId);
  if (!accum) return;
  const now = Date.now();
  const maxAgeMs = Math.max(1, Number(settings?.maxMinutes) || DEFAULT_TRAIL_SETTINGS.maxMinutes) * 60_000;
  const maxPoints = Math.max(2, Number(settings?.maxPoints) || DEFAULT_TRAIL_SETTINGS.maxPoints);
  while (accum.length > 0 && now - accum[0].time > maxAgeMs) accum.shift();
  while (accum.length > maxPoints) accum.shift();
}

function rebuildPolylineFromAccumulated(deviceId) {
  const accum = accumulatedTrail.get(deviceId);
  if (!accum || accum.length < 2) {
    snappedPolyline.delete(deviceId);
    return;
  }
  snappedPolyline.set(deviceId, {
    type: 'LineString',
    coordinates: accum.map((p) => [p.lng, p.lat]),
  });
}

async function callMatch(deviceId, trailSettings) {
  const buf = buffers.get(deviceId);
  if (!buf || buf.length < 2) return;
  inFlight.add(deviceId);
  const points = buf.map((p) => ({ lng: p.lng, lat: p.lat, time: p.time }));
  const lastInputTime = points[points.length - 1].time;
  const sentLen = buf.length;
  try {
    const res = await snapApi.match(points);
    lastCallAt.set(deviceId, Date.now());
    lastSentCount.set(deviceId, sentLen);

    // Accumulate snapped tracepoints into a per-device history. We append only
    // tracepoints corresponding to input fixes NEWER than the latest already in
    // the accumulator — successive OSRM batches overlap (the buffer slides
    // forward 5 fixes at a time), so blind appending would double-count. The
    // accumulator is then trimmed by the same maxMinutes/maxPoints caps the
    // raw `useTrails` history uses, so changing the Trails settings affects
    // both rails consistently.
    if (Array.isArray(res?.matched) && res.matched.length === points.length) {
      let accum = accumulatedTrail.get(deviceId);
      if (!accum) {
        accum = [];
        accumulatedTrail.set(deviceId, accum);
      }
      const lastAccumTime = accum.length > 0 ? accum[accum.length - 1].time : 0;
      for (let i = 0; i < points.length; i++) {
        const inputTime = points[i].time;
        if (inputTime <= lastAccumTime) continue;
        const m = res.matched[i];
        // Drop OSRM "no-match" entries (NaN sentinels) — they'd anchor the
        // trail off-road. Fall back to the raw fix in that case so the
        // accumulator keeps growing temporally.
        const lng = Number.isFinite(m?.lng) ? m.lng : points[i].lng;
        const lat = Number.isFinite(m?.lat) ? m.lat : points[i].lat;
        accum.push({ lng, lat, time: inputTime });
      }
      trimAccumulated(deviceId, trailSettings);
      rebuildPolylineFromAccumulated(deviceId);

      // Surface the latest matched coord as the per-marker snap target. fixTime
      // is stamped on the LAST input we sent so MapView's projection knows how
      // far the marker has moved since.
      const latestAccum = accum[accum.length - 1];
      if (latestAccum) {
        snappedLatest.set(deviceId, {
          latitude: latestAccum.lat,
          longitude: latestAccum.lng,
          fixTime: lastInputTime,
        });
      }
    } else if (res?.geometry?.type === 'LineString' && Array.isArray(res.geometry.coordinates) && res.geometry.coordinates.length >= 2) {
      // Length mismatch but geometry exists — use it as-is for this batch
      // without accumulating (no per-tracepoint mapping we can stitch).
      snappedPolyline.set(deviceId, res.geometry);
    } else {
      // Last-resort fallback: emit a polyline through the raw input points so
      // the trail layer renders something even if OSRM returned nothing useful.
      const coords = points.map((p) => [p.lng, p.lat]);
      snappedPolyline.set(deviceId, { type: 'LineString', coordinates: coords });
    }
  } catch (err) {
    // Best-effort: log and back off via lastCallAt so we don't hammer a sick
    // OSRM on every WS tick. Clearing the cached snapped value would force the
    // marker to flicker back to raw — keep the last good snap instead.
    lastCallAt.set(deviceId, Date.now());
    if (typeof console !== 'undefined' && console?.warn) {
      console.warn('[snap] /match failed for device', deviceId, err?.message || err);
    }
  } finally {
    inFlight.delete(deviceId);
  }
}

/**
 * Per-user toggle for snap-to-road, mirroring `useLiveMapAnimate`. Only
 * meaningful when the org has `featureSnapToRoad` enabled — the pill itself
 * hides for orgs without the flag, so this hook returning `true` doesn't
 * spam OSRM on disabled orgs (gate is in `useSnappedTrails`).
 *
 * Default ON when the org enables snap: the org admin opted in for the
 * tenant, so the most expected behavior is "snapping is on by default";
 * power users can opt out per browser.
 *
 * @returns {[boolean, () => void]}
 */
export function useLiveMapSnap() {
  const [enabled, setEnabled] = useState(() => {
    try {
      const v = localStorage.getItem(LS_KEY);
      return v == null ? true : v === 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, String(enabled));
    } catch {
      /* private-mode safari etc. — non-fatal */
    }
  }, [enabled]);

  const toggle = useCallback(() => setEnabled((p) => !p), []);

  return [enabled, toggle];
}

function readUserPrefSync() {
  try {
    const v = localStorage.getItem(LS_KEY);
    return v == null ? true : v === 'true';
  } catch {
    return true;
  }
}

/**
 * Per-user threshold (meters) for the snap projection. Returns
 * `[value, setValue, { min, max, defaultValue }]`. The setter writes to
 * localStorage and triggers re-renders for any subscriber via the same
 * storage-event + visibilitychange path used by the main hook.
 */
export function useLiveMapSnapThreshold() {
  const [value, setValue] = useState(readThresholdSync);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === LS_THRESHOLD_KEY) setValue(readThresholdSync());
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const set = useCallback((n) => {
    const next = clampThreshold(Number(n));
    setValue(next);
    try {
      localStorage.setItem(LS_THRESHOLD_KEY, String(next));
    } catch {
      /* non-fatal */
    }
  }, []);

  return [value, set, { min: MIN_THRESHOLD_M, max: MAX_THRESHOLD_M, defaultValue: DEFAULT_THRESHOLD_M }];
}

/**
 * Passive readout of how many vehicles are currently glued to the snapped road
 * versus how many have a road match at all — backs the "X / Y vehicles snapped
 * to road" line in the Live Map snap gear popover.
 *
 * Subscribes to the module-scope snap pub/sub (re-renders when a batch lands)
 * but does NOT run the OSRM batching effects — that engine is owned by the
 * single `useSnappedTrails()` mount inside MapView. The threshold is passed in
 * from the popover's own slider state so the count responds live to drags
 * (same-tab localStorage writes don't fire `storage` events, so a second
 * `useLiveMapSnapThreshold()` instance wouldn't see them).
 *
 * @param {number} thresholdM — current projection cap, from the caller's slider
 * @returns {{ snapped: number, matched: number }}
 */
export function useSnapCounts(thresholdM) {
  const version = useSyncExternalStore(subscribeSnap, getSnapVersion, getSnapVersion);
  const positions = useSelector((state) => state.session.positions);
  return useMemo(
    () => countSnapped(positions, snappedPolyline, thresholdM),
    [positions, thresholdM, version],
  );
}

/**
 * Hook returning the latest snapped position + polyline per device.
 *
 * Returns:
 *   - enabled:        boolean — true iff BOTH the org flag (`featureSnapToRoad`)
 *                     AND the per-user pill toggle are on. MapView treats this
 *                     as "do the overlay" and falls through to raw when false.
 *   - snappedById:    Map<deviceId, { latitude, longitude }>
 *   - polylineById:   Map<deviceId, GeoJSON.LineString>
 *   - tick:           number that increments whenever a new snap result lands
 *   - thresholdM:     per-user snap-projection cap (meters). MapView projects
 *                     the marker onto the polyline when distance <= this value;
 *                     above, falls through to raw so the marker keeps moving.
 *
 * Callers read the Maps lazily (cheap) and re-render on `tick` change.
 */
export default function useSnappedTrails() {
  const { snapToRoad: orgFlag } = useFeatures();
  // Subscribe to the per-user pref so the hook re-renders when the pill is
  // toggled in the same tab. Cross-tab updates land via the storage event in
  // `useLiveMapSnap`'s `setEnabled` consumer (same tab gets state update).
  const [userPref, setUserPref] = useState(readUserPrefSync);
  const [thresholdM, setThresholdM] = useState(readThresholdSync);
  useEffect(() => {
    const handler = (e) => {
      if (e.key === LS_KEY) setUserPref(e.newValue == null ? true : e.newValue === 'true');
      if (e.key === LS_THRESHOLD_KEY) setThresholdM(readThresholdSync());
    };
    window.addEventListener('storage', handler);
    // Also poll once per visibility-change in case localStorage was written in
    // the same tab but a different module mounted this hook later.
    const onVis = () => {
      setUserPref(readUserPrefSync());
      setThresholdM(readThresholdSync());
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('storage', handler);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const snapToRoad = orgFlag && userPref;
  const positions = useSelector((state) => state.session.positions);
  const devices = useSelector((state) => state.devices.items);
  const [tick, setTick] = useState(0);
  const [trailSettings, setTrailSettings] = useState(readTrailSettingsSync);
  const drainTimer = useRef(null);

  // Subscribe to the same trail settings localStorage key `useTrails` writes
  // to — so when the user adjusts maxPoints / maxMinutes via the Trails gear
  // popover, the snapped trail cap follows automatically. Same-tab updates
  // need the visibility check because storage events don't fire for the
  // writing tab.
  useEffect(() => {
    const refresh = () => setTrailSettings(readTrailSettingsSync());
    const handler = (e) => {
      if (e.key === TRAILS_SETTINGS_KEY) refresh();
    };
    window.addEventListener('storage', handler);
    document.addEventListener('visibilitychange', refresh);
    // Re-poll on a slow interval as a safety net — useTrails writes via
    // `useEffect(setItem)` which doesn't fire `storage` for the same tab.
    const iv = setInterval(refresh, 5_000);
    return () => {
      window.removeEventListener('storage', handler);
      document.removeEventListener('visibilitychange', refresh);
      clearInterval(iv);
    };
  }, []);

  // Re-trim accumulated trails whenever settings shrink — otherwise an older
  // history would persist visually until the next OSRM batch lands.
  useEffect(() => {
    if (!snapToRoad) return;
    for (const deviceId of accumulatedTrail.keys()) {
      trimAccumulated(deviceId, trailSettings);
      rebuildPolylineFromAccumulated(deviceId);
    }
    setTick((n) => n + 1);
    notifySnap();
  }, [trailSettings, snapToRoad]);

  useEffect(() => {
    if (!snapToRoad) return undefined;

    // Ingest raw fixes into per-device buffers, skipping stationary fixes for
    // the same reason useTrails does — they pollute the matched polyline.
    let changed = false;
    for (const [deviceIdStr, pos] of Object.entries(positions)) {
      const deviceId = Number(deviceIdStr);
      const device = devices[deviceId];
      if (!device || !pos) continue;
      if (!Number.isFinite(pos.latitude) || !Number.isFinite(pos.longitude)) continue;
      if (pos.latitude === 0 && pos.longitude === 0) continue;
      if (isStationaryFix(pos)) continue;
      const time = pos.fixTime ? new Date(pos.fixTime).getTime() : Date.now();
      if (appendToBuffer(deviceId, pos.longitude, pos.latitude, time)) {
        changed = true;
      }
    }
    if (!changed) return undefined;

    if (drainTimer.current) return undefined;
    drainTimer.current = setTimeout(async () => {
      drainTimer.current = null;
      const now = Date.now();
      const candidates = [];
      for (const deviceId of buffers.keys()) {
        if (shouldCall(deviceId, now)) candidates.push(deviceId);
      }
      const slice = candidates.slice(0, MAX_PER_TICK);
      await Promise.all(slice.map((id) => callMatch(id, trailSettings)));
      setTick((n) => n + 1);
      notifySnap();
    }, FETCH_DEBOUNCE_MS);

    return undefined;
  }, [snapToRoad, positions, devices, trailSettings]);

  // Wipe state when the org disables the flag — otherwise stale snaps could
  // leak into the next render after toggle-off.
  useEffect(() => {
    if (snapToRoad) return;
    buffers.clear();
    lastCallAt.clear();
    lastSentCount.clear();
    snappedLatest.clear();
    snappedPolyline.clear();
    accumulatedTrail.clear();
    setTick((n) => n + 1);
    notifySnap();
  }, [snapToRoad]);

  return useMemo(
    () => ({
      enabled: !!snapToRoad,
      snappedById: snappedLatest,
      polylineById: snappedPolyline,
      tick,
      thresholdM,
    }),
    [snapToRoad, tick, thresholdM],
  );
}
